import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { useAuth } from '../lib/auth-context';
import { apiPost, ApiError } from '../lib/api';

// Unified sign-in. One URL serves both clients and staff — after the magic-code
// verifies, we route the user based on their `kind` on the session: 'staff'
// goes to /admin, 'client' goes to /portal. The two portal-specific pages this
// replaces (/portal/sign-in and /admin/sign-in) now redirect here.

interface RequestCodeResp { ok: true; ttl_seconds: number }

export function SignIn() {
  const { user, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // If something nudged us here with a remembered URL (e.g. RequireAuth on a
  // protected route), only honour it when its portal matches the resolved
  // user's kind. Mismatch = land on kind home instead — avoids a visible
  // double redirect through RequireAuth.
  const requestedFrom = (location.state as { from?: string } | null)?.from;

  const [stage, setStage] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [ttl, setTtl] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    if (stage !== 'code' || ttl <= 0) return;
    const id = window.setInterval(() => setTtl((t) => (t > 0 ? t - 1 : 0)), 1000);
    return () => window.clearInterval(id);
  }, [stage, ttl]);

  // If the user is already signed in, send them to the right portal home.
  useEffect(() => {
    if (!user) return;
    const home = user.kind === 'staff' ? '/admin' : '/portal';
    // Only honour requestedFrom if it points inside the matching portal —
    // otherwise we'd bounce through RequireAuth a second time.
    const allowedFrom =
      requestedFrom &&
      ((user.kind === 'staff' && requestedFrom.startsWith('/admin')) ||
        (user.kind === 'client' && requestedFrom.startsWith('/portal')))
        ? requestedFrom
        : home;
    navigate(allowedFrom, { replace: true });
  }, [user, navigate, requestedFrom]);

  const submitEmail = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await apiPost<RequestCodeResp>('/api/auth/request-code', { email });
      setTtl(r.ttl_seconds);
      setStage('code');
    } catch (err) {
      setError(err instanceof ApiError ? err.serverMessage : 'Could not send code. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resendCode = async () => {
    if (resending || submitting) return;
    setResending(true);
    setError(null);
    setInfo(null);
    try {
      const r = await apiPost<RequestCodeResp>('/api/auth/request-code', { email });
      setTtl(r.ttl_seconds);
      setCode('');
      setInfo('A new code has been sent.');
    } catch (err) {
      setError(err instanceof ApiError ? err.serverMessage : 'Could not resend code.');
    } finally {
      setResending(false);
    }
  };

  const submitCode = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiPost('/api/auth/verify-code', { email, code: code.replace(/\s+/g, '') });
      // refresh() updates the AuthProvider's `user` — the effect above then
      // navigates them to the right portal based on `kind`.
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.serverMessage : 'Invalid code.');
    } finally {
      setSubmitting(false);
    }
  };

  const minutes = Math.floor(ttl / 60).toString().padStart(2, '0');
  const seconds = (ttl % 60).toString().padStart(2, '0');

  // Pre-render guards so the user never sees the form when they shouldn't.
  // - `loading` means the auth-context's /api/auth/me probe is still in flight.
  // - `user` set means the post-sign-in effect above is about to redirect; we
  //   render a placeholder instead of flashing the form for a split second.
  if (loading || user) {
    return (
      <main className="auth-shell">
        <div className="auth-card">
          <div className="auth-card-head">
            <Link to="/" aria-label="Omega Trade home" className="auth-logo"><Logo /></Link>
          </div>
          <div className="sub" style={{ textAlign: 'center' }}>
            {user ? 'Signing you in…' : 'Checking session…'}
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="auth-shell">
      <div className="auth-card">
        <div className="auth-card-head">
          <Link to="/" aria-label="Omega Trade home" className="auth-logo"><Logo /></Link>
          <h1>Sign in</h1>
          <div className="sub">
            Enter your email. We'll send you a six-digit code and route you
            to your portal — client or operator — based on your account.
          </div>
        </div>

        {stage === 'email' ? (
          <form onSubmit={submitEmail}>
            <div className="field">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoFocus
              />
              <div className="hint">
                A six-digit code will be sent to this address.
              </div>
            </div>
            {error && <div style={{ color: 'var(--red)', fontSize: 13 }}>{error}</div>}
            <button
              className="btn accent"
              type="submit"
              disabled={submitting}
            >
              {submitting ? 'Sending…' : 'Continue →'}
            </button>
            <div style={{ fontSize: 12, color: 'var(--fg-mute)', marginTop: 4, lineHeight: 1.6 }}>
              Body-corporate-only platform. By continuing you confirm you're the
              authorised contact for a client of Omega Trade and Finance Ltd, or an
              Omega staff member, and you accept the Terms of Business.
            </div>
            <div style={{ fontSize: 13, color: 'var(--fg-mute)', lineHeight: 1.6 }}>
              New corporate client?{' '}
              <Link className="apply-portal-link" to="/portal/sign-up" style={{ color: 'var(--accent)' }}>
                Apply for portal access →
              </Link>
            </div>
          </form>
        ) : (
          <form onSubmit={submitCode}>
            <div className="field">
              <label>Verification code</label>
              <input
                inputMode="numeric"
                pattern="[0-9 ]*"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                autoFocus
                required
              />
              <div className="hint">
                Sent to {email} · expires in {minutes}:{seconds}
              </div>
            </div>
            {error && <div style={{ color: 'var(--red)', fontSize: 13 }}>{error}</div>}
            {info && <div style={{ color: 'var(--accent)', fontSize: 13 }}>{info}</div>}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button className="btn accent" type="submit" disabled={submitting}>
                {submitting ? 'Verifying…' : 'Sign in →'}
              </button>
              <button
                className="btn ghost"
                type="button"
                onClick={resendCode}
                disabled={resending || ttl > 0}
                title={ttl > 0 ? 'Wait until current code expires' : 'Send a new code'}
              >
                {resending ? 'Resending…' : 'Resend code'}
              </button>
              <button
                className="btn ghost"
                type="button"
                onClick={() => { setStage('email'); setCode(''); setError(null); setInfo(null); }}
              >
                Back
              </button>
            </div>
            <div style={{ fontSize: 12, color: 'var(--fg-mute)', lineHeight: 1.55 }}>
              Didn't get a code? Check your spam folder, then either resend or go
              back to correct the email. Codes only go to active accounts —
              if you've just applied for access, wait for compliance to activate
              your account before signing in.
            </div>
          </form>
        )}

        <div className="auth-card-foot">
          Omega Trade and Finance Ltd is not authorised or regulated by the FCA.
          All sign-ins are audit-logged.
        </div>
      </div>
    </main>
  );
}
